// LocalStoreBookingCalendar.tsx
import React, { useEffect, useMemo, useState } from 'react';
import './LocalStoreBookingCalendar.scss';
import { IconButton, CircularProgress, Typography } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import EventBusyRoundedIcon from '@material-ui/icons/EventBusyRounded';
import CalendarTodayRoundedIcon from '@material-ui/icons/CalendarTodayRounded';

import { http } from '../fx-core';

export interface CalendarSlot {
  date: string;      // yyyy-MM-dd
  startHour: number;
  durationHours: number;
  label: string;      // full display string, e.g. "Fri 05 Sept, 11:00 AM – 1:00 PM"
}

interface AvailableStart {
  hour: number;
  label: string;
  isAvailable: boolean;
}

interface Props {
  communityId: number;
  minDate?: string; // yyyy-MM-dd — usually today
  onSlotsChange: (slots: CalendarSlot[]) => void;
  // Fires whenever the selected date range changes, with every date (yyyy-MM-dd) currently
  // in range. Lets the parent validate that a slot has been picked for each selected date.
  onRangeChange?: (dates: string[]) => void;
}

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_HEADERS = ['M','T','W','T','F','S','S'];
const DURATION_OPTIONS = [1, 2, 4, 8];

const toKey = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const shortLabel = (dateKey: string) =>
  new Date(dateKey).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });

const formatHourRange = (startHour: number, durationHours: number) => {
  const fmt = (h: number) => {
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${displayHour}:00 ${period}`;
  };
  return `${fmt(startHour)} – ${fmt(startHour + durationHours)}`;
};

// Collapses adjacent/overlapping picks on the same date into single
// continuous slots — e.g. picking 12-1pm and 1-2pm becomes one 12-2pm slot.
// This is what actually gets submitted, so a merchant never ends up with
// multiple back-to-back slots needing separate QR activations.
const mergeAdjacentSlots = (slots: CalendarSlot[]): CalendarSlot[] => {
  const byDate = new Map<string, { startHour: number; endHour: number }[]>();

  slots.forEach(s => {
    const list = byDate.get(s.date) || [];
    list.push({ startHour: s.startHour, endHour: s.startHour + s.durationHours });
    byDate.set(s.date, list);
  });

  const merged: CalendarSlot[] = [];
  byDate.forEach((intervals, date) => {
    intervals.sort((a, b) => a.startHour - b.startHour);
    let current = { ...intervals[0] };

    for (let i = 1; i < intervals.length; i++) {
      const next = intervals[i];
      if (next.startHour <= current.endHour) {
        // Touching or overlapping — extend the current merged block.
        current.endHour = Math.max(current.endHour, next.endHour);
      } else {
        merged.push({
          date,
          startHour: current.startHour,
          durationHours: current.endHour - current.startHour,
          label: `${shortLabel(date)}, ${formatHourRange(current.startHour, current.endHour - current.startHour)}`
        });
        current = { ...next };
      }
    }
    merged.push({
      date,
      startHour: current.startHour,
      durationHours: current.endHour - current.startHour,
      label: `${shortLabel(date)}, ${formatHourRange(current.startHour, current.endHour - current.startHour)}`
    });
  });

  return merged.sort((a, b) => a.date === b.date ? a.startHour - b.startHour : a.date.localeCompare(b.date));
};

const LocalStoreBookingCalendar: React.FC<Props> = ({ communityId, minDate, onSlotsChange, onRangeChange }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  // Range selection: click 1 = start, click 2 = end.
  const [rangeStart, setRangeStart] = useState<string | null>(null);
  const [rangeEnd, setRangeEnd] = useState<string | null>(null);

  const [durationHours, setDurationHours] = useState(2);
  const [startsByDate, setStartsByDate] = useState<Record<string, AvailableStart[]>>({});
  const [loadingDates, setLoadingDates] = useState<Set<string>>(new Set());

  // Confirmed picks — a date can have multiple picked hours. Stores the raw
  // hour-by-hour picks, since that's what drives which individual chips show
  // as "picked" in the UI — merging only happens for the outward-facing
  // summary/submission.
  const [chosenSlots, setChosenSlots] = useState<CalendarSlot[]>([]);

  const minDateObj = minDate ? new Date(minDate) : today;
  minDateObj.setHours(0, 0, 0, 0);

  const daysInGrid = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const leadingBlanks = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < leadingBlanks; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));
    return cells;
  }, [viewYear, viewMonth]);

  const rangeDates = useMemo(() => {
    if (!rangeStart) return [];
    const start = new Date(rangeStart);
    const end = rangeEnd ? new Date(rangeEnd) : start;
    const [from, to] = start <= end ? [start, end] : [end, start];
    const dates: string[] = [];
    const cursor = new Date(from);
    while (cursor <= to) {
      dates.push(toKey(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  }, [rangeStart, rangeEnd]);

  // Visual boundaries for the pill-shaped range highlight — based on chronological
  // order in rangeDates, not click order, so the pill always renders left → right.
  const visualStartKey = rangeDates[0] ?? null;
  const visualEndKey = rangeDates[rangeDates.length - 1] ?? null;

  const goPrevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
  const goNextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };

  const isPast = (d: Date) => d < minDateObj;

  const handleDayClick = (date: Date) => {
    if (isPast(date)) return;
    const key = toKey(date);

    if (!rangeStart || (rangeStart && rangeEnd)) {
      // Starting a fresh range
      setRangeStart(key);
      setRangeEnd(null);
    } else {
      // Completing the range
      setRangeEnd(key);
    }
  };

  const fetchStartsForDate = (dateKey: string) => {
    if (!communityId) return;
    setLoadingDates(prev => new Set(prev).add(dateKey));
    http.get({ url: `localstore/${communityId}/available-starts?date=${dateKey}&durationHours=${durationHours}` })
      .then((res: any) => {
        setStartsByDate(prev => ({ ...prev, [dateKey]: res.data?.starts || [] }));
      })
      .catch(() => setStartsByDate(prev => ({ ...prev, [dateKey]: [] })))
      .finally(() => {
        setLoadingDates(prev => {
          const next = new Set(prev);
          next.delete(dateKey);
          return next;
        });
      });
  };

  // Whenever the selected range changes: drop cached availability + picks for dates
  // that fell out of range, fetch availability for every date now in range so all of
  // them can show their hourly columns side by side, and tell the parent which dates
  // are currently selected so it can validate a slot was picked for each one.
  useEffect(() => {
    const rangeSet = new Set(rangeDates);

    setChosenSlots(prev => {
      const next = prev.filter(s => rangeSet.has(s.date));
      if (next.length !== prev.length) onSlotsChange(mergeAdjacentSlots(next));
      return next;
    });

    setStartsByDate(prev => {
      const next: Record<string, AvailableStart[]> = {};
      Object.keys(prev).forEach(d => { if (rangeSet.has(d)) next[d] = prev[d]; });
      return next;
    });

    rangeDates.forEach(d => fetchStartsForDate(d));
    onRangeChange?.(rangeDates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDates.join(',')]);

  // Duration applies to every date at once, so changing it refetches all of them.
  useEffect(() => {
    if (rangeDates.length === 0) return;
    // Picks made with the previous duration are no longer valid for the new
    // slot grid, so do not leave them selected or submit them accidentally.
    setChosenSlots([]);
    onSlotsChange([]);
    rangeDates.forEach(d => fetchStartsForDate(d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationHours]);

  const toggleSlotPick = (dateKey: string, start: AvailableStart) => {
    if (!start.isAvailable) return;

    setChosenSlots(prev => {
      const exists = prev.some(s => s.date === dateKey && s.startHour === start.hour);
      let next: CalendarSlot[];
      if (exists) {
        next = prev.filter(s => !(s.date === dateKey && s.startHour === start.hour));
      } else {
        const label = `${shortLabel(dateKey)}, ${formatHourRange(start.hour, durationHours)}`;
        // Only one time block may be selected per date. Choosing a different
        // block replaces the earlier choice; additional blocks require a
        // separate booking. This applies to every supported duration.
        next = [
          ...prev.filter(s => s.date !== dateKey),
          { date: dateKey, startHour: start.hour, durationHours, label }
        ];
      }
      onSlotsChange(mergeAdjacentSlots(next));
      return next;
    });
  };

  const removeChosenSlot = (dateKey: string, startHour: number) => {
    setChosenSlots(prev => {
      const next = prev.filter(s => !(s.date === dateKey && s.startHour === startHour));
      onSlotsChange(mergeAdjacentSlots(next));
      return next;
    });
  };

  const clearRange = () => {
    setRangeStart(null);
    setRangeEnd(null);
    setChosenSlots([]);
    onSlotsChange([]);
  };

  const chosenCountForDate = (dateKey: string) => chosenSlots.filter(s => s.date === dateKey).length;

  const mergedSlots = mergeAdjacentSlots(chosenSlots);

  return (
    <div className="lsc-calendar-ui">
      <div className="lsc-main-row">
        {/* ── Left: month calendar — range selection ── */}
        <div className="lsc-calendar-panel">
          <div className="lsc-month-nav">
            <IconButton className="lsc-nav-btn" size="small" onClick={goPrevMonth} aria-label="Previous month">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <span className="lsc-month-label">{MONTH_NAMES[viewMonth]} {viewYear}</span>
            <IconButton className="lsc-nav-btn" size="small" onClick={goNextMonth} aria-label="Next month">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </div>

          <div className="lsc-day-headers">
            {DAY_HEADERS.map((d, i) => <span key={`${d}-${i}`}>{d}</span>)}
          </div>

          <div className="lsc-grid">
            {daysInGrid.map((date, i) => {
              if (!date) return <div key={i} className="lsc-cell lsc-cell-blank" />;
              const key = toKey(date);
              const disabled = isPast(date);
              const inRange = rangeDates.includes(key);
              const isRangeStart = key === visualStartKey;
              const isRangeEnd = key === visualEndKey;
              const isToday = toKey(today) === key;

              return (
                <div
                  key={i}
                  className={[
                    'lsc-cell',
                    disabled ? 'lsc-cell-disabled' : '',
                    inRange ? 'lsc-cell-in-range' : '',
                    isRangeStart ? 'lsc-cell-range-start' : '',
                    isRangeEnd ? 'lsc-cell-range-end' : '',
                    isToday ? 'lsc-cell-today' : ''
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleDayClick(date)}
                  role="button"
                  aria-disabled={disabled}
                >
                  {date.getDate()}
                </div>
              );
            })}
          </div>

          {rangeStart && (
            <div className="lsc-range-summary">
              <span className="lsc-range-summary-text">
                {rangeEnd
                  ? `${shortLabel(visualStartKey!)} → ${shortLabel(visualEndKey!)} · ${rangeDates.length} day${rangeDates.length > 1 ? 's' : ''}`
                  : `${shortLabel(rangeStart)} selected — tap an end date`}
              </span>
              <button className="lsc-text-action" onClick={clearRange} type="button">Clear</button>
            </div>
          )}

          {rangeDates.length > 0 && (
            <div className="lsc-duration-field">
              <label className="lsc-field-label" htmlFor="lsc-duration-select">Slot length</label>
              <div className="lsc-select-wrap">
                <select
                  id="lsc-duration-select"
                  className="lsc-select"
                  value={durationHours}
                  onChange={(e) => setDurationHours(Number(e.target.value))}
                >
                  {DURATION_OPTIONS.map(h => (
                    <option key={h} value={h}>{h} hour{h > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* ── Right: every selected date gets its own scrollable hourly column ── */}
        <div className="lsc-slots-panel-multi">
          {rangeDates.length === 0 ? (
            <div className="lsc-slots-placeholder">
              <CalendarTodayRoundedIcon fontSize="small" />
              <span>Pick a date range on the left to see hourly availability for each day here.</span>
            </div>
          ) : (
            <div className="lsc-day-columns-scroll">
              {rangeDates.map((dateKey) => {
                const isLoading = loadingDates.has(dateKey);
                const allStarts = startsByDate[dateKey] || [];
                // The API reports every possible hourly start. Present a
                // non-overlapping grid anchored to the opening hour instead:
                // for two hours this is 9-11, 11-1, 1-3, etc.
                const starts = allStarts.filter((_, index) => index % durationHours === 0);
                const count = chosenCountForDate(dateKey);
                const hasAvailability = !isLoading && starts.some(s => s.isAvailable);
                const needsPick = hasAvailability && count === 0;

                return (
                  <div className={`lsc-day-column ${needsPick ? 'lsc-day-column-needs-pick' : ''}`} key={dateKey}>
                    <div className="lsc-day-column-header">
                      <span className="lsc-day-column-date">{shortLabel(dateKey)}</span>
                      {count > 0 && <span className="lsc-day-column-count">{count}</span>}
                    </div>

                    {needsPick && <div className="lsc-day-column-hint">Pick a time slot</div>}

                    <div className="lsc-day-column-body">
                      {isLoading && (
                        <div className="lsc-slots-loading">
                          <CircularProgress size={16} thickness={4} />
                        </div>
                      )}

                      {!isLoading && starts.length === 0 && (
                        <div className="lsc-slots-empty">
                          <EventBusyRoundedIcon fontSize="small" />
                          <span>No slots</span>
                        </div>
                      )}

                      {!isLoading && starts.map((start) => {
                        const picked = chosenSlots.some(s => s.date === dateKey && s.startHour === start.hour);
                        return (
                          <div
                            key={start.hour}
                            className={`lsc-slot-chip ${!start.isAvailable ? 'lsc-slot-chip-disabled' : ''} ${picked ? 'lsc-slot-chip-picked' : ''}`}
                            onClick={() => toggleSlotPick(dateKey, start)}
                            role="button"
                            aria-disabled={!start.isAvailable}
                          >
                            {start.label}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Summary of everything picked across the whole range, merged ── */}
      {chosenSlots.length > 0 && (
        <div className="lsc-selected-list">
          <Typography className="lsc-selected-title">
            Your booking ({mergedSlots.length} slot{mergedSlots.length > 1 ? 's' : ''})
          </Typography>
          {mergedSlots.map((slot) => (
            <div className="lsc-selected-row" key={`${slot.date}-${slot.startHour}`}>
              <span>{slot.label}</span>
            </div>
          ))}
          <Typography className="lsc-selected-hint">
            To remove time, reopen that date above and click the picked hour(s) again.
          </Typography>
        </div>
      )}
    </div>
  );
};

export default LocalStoreBookingCalendar;
